import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import brand from '../config/brand';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  Loader2,
  CheckCircle,
  MessageSquare,
  HelpCircle,
  Building2,
  Users,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'general',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const categories = [
    { value: 'general', label: t('contact.category_general', 'General Inquiry') },
    { value: 'property', label: t('contact.category_property', 'Property Question') },
    { value: 'booking', label: t('contact.category_booking', 'Booking Support') },
    { value: 'owner', label: t('contact.category_owner', 'Property Owner Inquiry') },
    { value: 'technical', label: t('contact.category_technical', 'Technical Support') },
    { value: 'partnership', label: t('contact.category_partnership', 'Partnership Opportunity') },
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate
    if (!formData.name || !formData.email || !formData.message) {
      setError(t('contact.error_required', 'Please fill in all required fields'));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError(t('contact.error_email', 'Please enter a valid email address'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setSubmitted(true);
      toast.success(t('contact.success', 'Message sent successfully!'));
    } catch (err) {
      setError(t('contact.error_send', 'Failed to send message. Please try again.'));
      toast.error(t('contact.error_send', 'Failed to send message'));
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: t('contact.email', 'Email'),
      value: brand.contact.support,
      link: `mailto:${brand.contact.support}`
    },
    {
      icon: Phone,
      title: t('contact.phone', 'Phone'),
      value: brand.contact.phone,
      link: `tel:${brand.contact.phone_href}`
    },
    {
      icon: MapPin,
      title: t('contact.address', 'Address'),
      value: t('contact.address_value', 'Kaiserslautern, Germany'),
      link: null
    },
    {
      icon: Clock,
      title: t('contact.hours', 'Business Hours'),
      value: t('contact.hours_value', 'Mon-Fri: 9:00 - 18:00 CET'),
      link: null
    }
  ];

  const quickLinks = [
    {
      icon: HelpCircle,
      title: t('contact.faq_title', 'FAQ'),
      description: t('contact.faq_desc', 'Find answers to common questions'),
      link: '/faq'
    },
    {
      icon: Building2,
      title: t('contact.for_owners', 'For Property Owners'),
      description: t('contact.for_owners_desc', 'Learn about listing your property'),
      link: '/register?role=owner'
    },
    {
      icon: Users,
      title: t('contact.about_us', 'About Us'),
      description: t('contact.about_us_desc', 'Learn more about our mission'),
      link: '/about'
    }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20" data-testid="contact-page-success">
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold text-slate-800 mb-4">
              {t('contact.thank_you', 'Thank You!')}
            </h1>
            <p className="text-slate-600 mb-8">
              {t('contact.thank_you_message', 'Your message has been received. Our team will get back to you within 24-48 hours.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({ name: '', email: '', subject: '', category: 'general', message: '' });
                }}
                variant="outline"
                className="border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                {t('contact.send_another', 'Send Another Message')}
              </Button>
              <Link to="/">
                <Button className="bg-signal-blue hover:bg-signal-blue-hover text-white">
                  {t('contact.back_home', 'Back to Home')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-signal-blue/10 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 relative text-center">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-800 mb-4">
            {t('contact.title', 'Get in Touch')}
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            {t('contact.subtitle', 'Have questions about TLA housing? Our team is here to help you find your perfect European home.')}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <MessageSquare className="w-6 h-6 text-signal-blue" />
                  <h2 className="font-heading text-2xl font-bold text-slate-800">
                    {t('contact.form_title', 'Send Us a Message')}
                  </h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-slate-700">
                        {t('contact.name', 'Full Name')} *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t('contact.name_placeholder', 'John Smith')}
                        className="mt-1 bg-white border-slate-300 text-slate-800"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-slate-700">
                        {t('contact.email_label', 'Email Address')} *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contact.email_placeholder', 'john@example.com')}
                        className="mt-1 bg-white border-slate-300 text-slate-800"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="category" className="text-slate-700">
                        {t('contact.category', 'Category')}
                      </Label>
                      <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="mt-1 w-full rounded-md bg-white border border-slate-300 text-slate-800 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-signal-blue"
                        data-testid="contact-category"
                      >
                        {categories.map(cat => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="subject" className="text-slate-700">
                        {t('contact.subject', 'Subject')}
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder={t('contact.subject_placeholder', 'How can we help?')}
                        className="mt-1 bg-white border-slate-300 text-slate-800"
                        data-testid="contact-subject"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-slate-700">
                      {t('contact.message', 'Message')} *
                    </Label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contact.message_placeholder', 'Tell us how we can assist you...')}
                      className="mt-1 w-full rounded-md bg-white border border-slate-300 text-slate-800 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-signal-blue resize-none"
                      data-testid="contact-message"
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white"
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('contact.sending', 'Sending...')}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {t('contact.send', 'Send Message')}
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-6">
              {/* Contact Details */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="font-heading text-lg font-bold text-slate-800 mb-4">
                  {t('contact.info_title', 'Contact Information')}
                </h3>
                <div className="space-y-4">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <info.icon className="w-5 h-5 text-signal-blue flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-slate-600 text-sm">{info.title}</p>
                        {info.link ? (
                          <a
                            href={info.link}
                            className="text-slate-700 hover:text-signal-blue transition-colors"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-slate-700">{info.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Links */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="font-heading text-lg font-bold text-slate-800 mb-4">
                  {t('contact.quick_links', 'Quick Links')}
                </h3>
                <div className="space-y-3">
                  {quickLinks.map((link, index) => (
                    <Link
                      key={index}
                      to={link.link}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                    >
                      <link.icon className="w-5 h-5 text-military-gold flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-slate-800 font-medium group-hover:text-signal-blue transition-colors">
                          {link.title}
                        </p>
                        <p className="text-slate-600 text-sm">{link.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Response Time Notice */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 border-signal-blue/30 bg-signal-blue/5">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-signal-blue flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-slate-700 font-medium">
                      {t('contact.response_title', 'Response Time')}
                    </p>
                    <p className="text-slate-600 text-sm">
                      {t('contact.response_text', 'We typically respond within 24-48 business hours. For urgent matters, please call our support line.')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section Placeholder */}
      <section className="py-12 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
            <MapPin className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-slate-800 mb-2">
              {t('contact.location_title', 'Our Location')}
            </h3>
            <p className="text-slate-600 mb-4">
              {t('contact.location_text', 'Based in Kaiserslautern, Germany - at the heart of the U.S. military community in Europe.')}
            </p>
            <p className="text-slate-800 text-sm">
              {t('contact.location_note', 'Serving military families across Germany, Italy, Spain, and beyond.')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
